"use client";

import { Compass, Box, Leaf, ArrowRight, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function Pricing() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pillars = [{
    index: "§ 01.1",
    icon: Compass,
    title: "OTA Revenue",
    italicWord: "Management",
    titleSuffix: "",
    description: "Maximize your visibility and conversion rates across all major OTAs with data-backed listing optimization and strategic rate management."
  }, {
    index: "§ 01.2",
    icon: Box,
    title: "Tech Stack",
    italicWord: "Implementation",
    titleSuffix: "",
    description: "Streamline your operations by integrating the right hospitality tech stack, from PMS to channel managers, tailored to your property's needs."
  }, {
    index: "§ 01.3",
    icon: Leaf,
    title: "Revenue",
    italicWord: "Audits",
    titleSuffix: "",
    description: "Identify hidden revenue leaks and growth opportunities with our comprehensive, deep-dive audits of your current pricing and distribution strategy."
  }];
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://hotelrevbaba.com/"
    }, {
      "@type": "ListItem",
      "position": 2,
      "name": "Our Services",
      "item": "https://hotelrevbaba.com/hotel-revenue-management-consulting/"
    }, {
      "@type": "ListItem",
      "position": 3,
      "name": "Dynamic Pricing & Forecasting Services",
      "item": "https://hotelrevbaba.com/dynamic-pricing-forecasting-services-2/"
    }]
  };
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Dynamic Pricing & Forecasting Services",
    "provider": {
      "@type": "LocalBusiness",
      "name": "HotelRevBaBa"
    },
    "description": "Data-driven revenue management solutions including OTA optimization, tech stack implementation, and comprehensive revenue audits for hotels.",
    "areaServed": "Worldwide",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Revenue Management Services",
      "itemListElement": [{
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "OTA Revenue Management"
        }
      }, {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Tech Stack Implementation"
        }
      }, {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Revenue Audits"
        }
      }]
    }
  };
  return <section data-section-id="2110" id="dynamicpricingengine" className="relative overflow-hidden bg-slate-50 py-28 md:py-36">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(breadcrumbSchema)
    }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(serviceSchema)
    }} />

      {}
      <nav className="relative z-10 mx-auto mb-12 max-w-[1440px] px-5 md:px-8 lg:px-14">
        <ol className="flex flex-wrap items-center gap-2 text-[10px] font-semibold tracking-[0.18em] uppercase text-slate-400">
          <li className="flex items-center gap-2">
            <Link to="/" className="transition-colors hover:text-secondary">Home</Link>
            <ChevronRight size={10} className="text-slate-300" />
          </li>
          <li className="flex items-center gap-2">
            <Link to="#" className="transition-colors hover:text-secondary">Our Services</Link>
            <ChevronRight size={10} className="text-slate-300" />
          </li>
          <li className="text-slate-600">Dynamic Pricing & Forecasting Services</li>
        </ol>
      </nav>

      <div aria-hidden="true" className="pointer-events-none absolute right-[-4%] top-[2%] hidden select-none md:block" style={{
      fontFamily: "var(--font-serif)",
      fontSize: "clamp(320px, 52vw, 760px)",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px hsl(222 47% 7%)",
      opacity: 0.03,
      fontWeight: 700
    }}>
        01
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute right-[-8%] top-[1%] block select-none md:hidden" style={{
      fontFamily: "var(--font-serif)",
      fontSize: "320px",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px hsl(222 47% 7%)",
      opacity: 0.03,
      fontWeight: 700
    }}>
        01
      </div>

      <div className="relative mx-auto max-w-[1440px] px-5 md:px-8 lg:px-14">
        <div ref={headerRef} className={`grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-6 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 uppercase" style={{
            fontFamily: "var(--font-sans)",
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.18em",
            lineHeight: 1,
            color: "hsl(43 65% 35%)"
          }}>
              <span>§ 01</span>
              <span className="text-slate-300">—</span>
              <span className="text-slate-500">CORE SERVICES / REVENUE STRATEGY</span>
            </div>

            <div className="mt-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <div className="flex items-center gap-1">
                <span className="h-1 w-1 rounded-lg bg-slate-900" />
                <span className="h-1 w-1 rounded-lg bg-slate-900" />
                <span className="h-1 w-1 rounded-lg bg-slate-900" />
              </div>
            </div>

            <h2 className="mt-8 font-default" style={{
            fontSize: "clamp(2.25rem, 4.8vw, 4.5rem)",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
            color: "hsl(222 47% 7%)"
          }}>
              Driving revenue through{" "}
              <br className="hidden sm:block" />
              expert{" "}
              <em style={{
              fontStyle: "italic",
              fontWeight: 400
            }}>
                precision
              </em>
              .
            </h2>
          </div>

          <div className="lg:col-span-5 lg:flex lg:items-end">
            <p className="font-default max-w-md text-slate-700" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            fontWeight: 400,
            lineHeight: 1.65
          }}>
              HotelRevBaBa delivers data-driven revenue management solutions designed to optimize your property's performance, increase direct bookings, and streamline your digital operations.
            </p>
          </div>
        </div>

        <div ref={cardsRef} className="mt-20 grid grid-cols-1 gap-6 md:gap-7 lg:grid-cols-12 lg:gap-6">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <div key={i} data-index={i} className={`lg:col-span-4 transition-all ease-out ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDuration: "720ms",
            transitionDelay: `${i * 80}ms`
          }}>
                <Card className="group relative h-full overflow-hidden p-9 transition-transform duration-300 ease-out hover:-translate-y-1 shadow-md" style={{
              backgroundColor: "white",
              color: "hsl(222 47% 7%)",
              borderRadius: "0.5rem",
              border: "1px solid hsl(215 16% 88%)"
            }}>
                  <div className="flex items-center gap-2 uppercase" style={{
                fontFamily: "var(--font-sans)",
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.18em",
                lineHeight: 1,
                color: "hsl(43 65% 35%)"
              }}>
                    <span>{pillar.index}</span>
                    <span className="text-slate-200">—</span>
                    <span className="text-slate-400">SERVICE</span>
                  </div>

                  <div className="mt-8 transition-transform duration-[400ms] ease-out group-hover:scale-110">
                    <Icon size={32} strokeWidth={1.5} style={{
                  color: "hsl(43 65% 35%)"
                }} />
                  </div>

                  <h3 className="mt-7 font-default" style={{
                fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)",
                fontWeight: 700,
                letterSpacing: "-0.012em",
                lineHeight: 1.2,
                color: "hsl(222 47% 7%)"
              }}>
                    {pillar.title}{" "}
                    <em style={{
                  fontStyle: "italic",
                  fontWeight: 400
                }}>
                      {pillar.italicWord}
                    </em>
                  </h3>

                  <p className="font-default mt-5 text-slate-600" style={{
                fontSize: "1rem",
                fontWeight: 400,
                letterSpacing: "0.005em",
                lineHeight: 1.65
              }}>
                    {pillar.description}
                  </p>

                  <div className="mt-9 flex items-center gap-3">
                    <div className="h-px flex-1 bg-slate-100" />
                  </div>

                  <div className="mt-6">
                    <Link to="#" className="group/link relative inline-flex items-center gap-2 font-default" style={{
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  color: "hsl(43 65% 35%)"
                }}>
                      <span className="relative">
                        View Details
                        <span className="absolute -bottom-0.5 left-0 h-px w-0 transition-all duration-300 ease-out group-hover/link:w-full bg-secondary" />
                      </span>
                      <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-300 group-hover/link:translate-x-1" />
                    </Link>
                  </div>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}